
import { useParams, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Navigation } from "@/components/Navigation";
import { MobileNavigation } from "@/components/MobileNavigation";
import { RelatedPins } from "@/components/pins/RelatedPins";
import { SocialMetaTags } from "@/components/SocialMetaTags";
import { PinDetailsLoadingView } from "@/components/pins/PinDetailsLoadingView";
import { PinDetailsErrorView } from "@/components/pins/PinDetailsErrorView";
import { PinDetailsContent } from "@/components/pins/PinDetailsContent";
import { usePinDetails } from "@/hooks/use-pin-details";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";

export default function PinDetails() {
  console.log("PinDetails component rendering");
  
  // Safe router params handling
  const navigate = useNavigate();
  const params = useParams<{ id: string }>();
  const [routerReady, setRouterReady] = useState(false);
  
  // Extract the pin ID from params
  const pinId = params?.id;
  
  // Use effect to safely handle router initialization
  useEffect(() => {
    if (params !== undefined) {
      setRouterReady(true);
      
      // If no ID is provided after router is ready, redirect to home
      if (!pinId) {
        console.error("No pin ID provided");
        navigate("/");
      }
    }
  }, [params, navigate, pinId]);

  const {
    pin,
    user,
    loading,
    loadError,
    comments,
    relatedPins,
    likeCount,
    userHasLiked,
    commentCount,
    collections,
    toggleLike,
    saveToCollection,
    handleCommentAdded,
    fetchComments
  } = usePinDetails(pinId);

  // Add preconnect for essential third-party domains
  useEffect(() => {
    const domains = [
      'https://api.supabase.com',
      'https://kcsydqftkfhmyewxtvgd.supabase.co'
    ];
    
    // Create preconnect links
    const links = domains.map(domain => {
      const link = document.createElement('link');
      link.rel = 'preconnect';
      link.href = domain;
      link.crossOrigin = 'anonymous';
      return link;
    });
    
    // Add to document head
    links.forEach(link => document.head.appendChild(link));
    
    // Cleanup function
    return () => {
      links.forEach(link => {
        if (document.head.contains(link)) {
          document.head.removeChild(link);
        }
      });
    };
  }, []);

  // Show loading state if router isn't ready yet
  if (!routerReady || !pinId) {
    return <PinDetailsLoadingView />;
  }
  
  if (loading) {
    return <PinDetailsLoadingView />;
  }

  if (loadError || !pin) {
    return <PinDetailsErrorView error={loadError} />;
  }

  return (
    <div className="min-h-screen pb-20">
      <Helmet>
        {/* Add preload for critical resources */}
        <link 
          rel="preload" 
          href={pin.image_url} 
          as="image" 
          type={pin.image_url.includes('.webp') ? 'image/webp' : 'image/jpeg'}
        />
        {/* Add cache control hints */}
        <meta httpEquiv="Cache-Control" content="max-age=3600" />
      </Helmet>
      
      <SocialMetaTags
        title={pin.title || "InkWebtoons"}
        description={pin.description || "Confira esta incrível arte no InkWebtoons"}
        imageUrl={pin.image_url}
        url={window.location.href}
        contentType="pin"
        contentId={pinId || ""}
      />
      
      <Navigation />
      <div className="container mx-auto px-4 pt-16 pb-16 max-w-screen-2xl">
        {/* Back button */}
        <div className="mb-6">
          <Button 
            variant="ghost" 
            onClick={() => navigate(-1)}
            className="flex items-center gap-1 hover:bg-gray-100 dark:hover:bg-gray-800"
            aria-label="Voltar para a página anterior"
          >
            <ArrowLeft className="h-4 w-4" /> Voltar
          </Button>
        </div>
        
        <PinDetailsContent
          pin={pin}
          user={user}
          likeCount={likeCount}
          commentCount={commentCount}
          userHasLiked={userHasLiked}
          collections={collections}
          comments={comments}
          pinId={pinId || ""}
          saveToCollection={saveToCollection}
          onLikeToggle={toggleLike}
          onCommentAdded={handleCommentAdded}
          refreshComments={fetchComments}
        />

        {relatedPins.length > 0 && (
          <div className="mt-12 pt-6 border-t border-gray-200">
            <RelatedPins pins={relatedPins} />
          </div>
        )}
      </div>
      <MobileNavigation />
    </div>
  );
}
